import { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, FileText, Check, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Smart Modal für das Sprachdiktat: zeigt den KI-korrigierten Text (bearbeitbar)
 * und erlaubt das Umschalten zur ursprünglichen Roh-Transkription. Beim Bestätigen
 * wird der finale Text via onConfirm zurückgegeben (→ Einfügen ins Ausgangsfeld).
 */
export function VoiceRefineModal({ open, onOpenChange, result, onConfirm }) {
  const [view, setView] = useState("corrected"); // corrected | raw
  const [text, setText] = useState("");

  useEffect(() => {
    if (result) { setView("corrected"); setText(result.corrected || ""); }
  }, [result]);

  // Status der KI-Nachbearbeitung für ein transparentes Badge
  const status = useMemo(() => {
    if (!result) return null;
    const raw = (result.raw || "").trim();
    const corrected = (result.corrected || "").trim();
    if (result.refined === false) {
      return {
        key: "unavailable",
        label: "KI nicht verfügbar – Rohtext",
        className: "bg-amber-50 text-amber-700 border-amber-200",
        Icon: AlertTriangle,
      };
    }
    if (raw && corrected && raw === corrected) {
      return {
        key: "unchanged",
        label: "Bereits sauber – unverändert",
        className: "bg-slate-50 text-slate-600 border-slate-200",
        Icon: CheckCircle2,
      };
    }
    return {
      key: "refined",
      label: "KI-optimiert",
      className: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Icon: Sparkles,
    };
  }, [result]);

  const switchView = (next) => {
    if (next === view) return;
    setText(next === "corrected" ? (result?.corrected || "") : (result?.raw || ""));
    setView(next);
  };

  const confirm = () => {
    const finalText = (text || "").trim();
    if (finalText) onConfirm(finalText);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="voice-refine-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Diktat prüfen
          </DialogTitle>
          <DialogDescription>
            KI-optimierte Fassung – Sie können den Text bearbeiten und zwischen korrigierter
            und ursprünglicher Version wechseln.
          </DialogDescription>
        </DialogHeader>

        {status && (
          <div
            className={`inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-xs font-medium ${status.className}`}
            data-testid={`voice-refine-status-${status.key}`}
          >
            <status.Icon className="w-3.5 h-3.5" />
            {status.label}
          </div>
        )}

        <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40 w-fit">
          <button
            type="button"
            onClick={() => switchView("corrected")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${view === "corrected" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="voice-view-corrected"
          >
            <Sparkles className="w-3.5 h-3.5" /> Korrigiert
          </button>
          <button
            type="button"
            onClick={() => switchView("raw")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${view === "raw" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
            data-testid="voice-view-raw"
          >
            <FileText className="w-3.5 h-3.5" /> Rohtext
          </button>
        </div>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={9}
          className="resize-y font-normal leading-relaxed"
          placeholder="Diktierter Text…"
          data-testid="voice-refine-textarea"
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="voice-refine-cancel">
            Abbrechen
          </Button>
          <Button type="button" onClick={confirm} disabled={!text.trim()} data-testid="voice-refine-confirm">
            <Check className="w-4 h-4 mr-2" /> Übernehmen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
